// medplum-patients.js — ONLINE-PRIMARY, same recipe as queue/reception.
// Patients.jsx admits both CLINICAL and front-desk roles; either reads as the
// ONE logged-in session — RLS scopes what each sees.
//
// PAGINATION (2026-07-06): the directory is SERVER-SIDE PAGED. The old query
// (Patient?_revinclude=Encounter:subject&_count=1000) pulled EVERY patient in
// one payload — measured 913 KB / 865 entries for 853 patients. We now fetch
// ONE page of 20 patients at a time (~21 KB) with an accurate total, and let
// the visible page's own encounters ride in via _revinclude (which, on a paged
// Patient query, only pulls the 20 shown patients' encounters — measured
// ~21 KB total, a single round-trip, marginally CHEAPER than a separate batch
// Encounter fetch, so we keep it to one query).
//
// Two coherent scopes, each a distinct server query (see Patients.jsx ScopeToggle):
//   • 'all'  → every patient, PAGED (Patient?_count=20&_offset=N&_sort=name).
//   • 'mine' → "My department" = patients who have an Encounter served by MY
//              department Organization, PAGED exactly like 'all'.
//
// FUNCTIONAL DEPT FILTER, NOT an RLS assumption (2026-07-07 fix): the earlier
// "'mine' = Encounter?_count=1000&_include=Encounter:subject, RLS-bounded to my
// dept" was WRONG on the cloud demo, where doctors bind the broad main-1
// `opd-doctor` AccessPolicy (no `%department` criterion) — so that query
// returned EVERY department's patients (the "ortho patients for a non-ortho
// doctor" bug) and never paginated. We now filter EXPLICITLY server-side:
//   Patient?_has:Encounter:subject:service-provider=Organization/<myDeptId>
// i.e. "patients who have an Encounter whose service-provider is my department
// Organization". This is CORRECT regardless of how broad the bound AccessPolicy
// is — the reverse-chain is the functional department filter; RLS remains the
// independent security lock (this is scoping a query, NOT an app-side security
// re-check — see AGENTS.md §2). The dept Organization id is resolved from the
// logged-in user's derived department slug (deriveCurrentUser) via a one-time
// Organization?identifier=<slug> lookup. A `_has` on a paged Patient query pages
// PATIENTS (not encounters), so "of N" counts distinct department patients and a
// patient with several dept visits still occupies exactly one row on one page.
//
// SEARCH is server-side too. In 'all' it spans every patient; in 'mine' it is
// ANDed with the department reverse-chain (both are Patient params) so search
// stays scoped to the department. Either way it is PAGED and resets to page 1.
import { getMedplum, deriveCurrentUser } from './medplum-client.js';
import { groupPatientBundle, shapePatientRow } from './shape/patients-view.js';

export const PATIENTS_PAGE_SIZE = 20;
const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';

// Resolves the logged-in user's department Organization id from their derived
// department SLUG (deriveCurrentUser). Every staff AccessPolicy grants
// Organization read (no criteria), so this one lookup is always allowed.
// Returns null when the user has no department (owner/admin/front-desk) — the
// caller then has no 'mine' set to show. Memoized per session (the slug→id
// mapping is stable for the life of the login).
let deptOrgIdCache = null; // { slug, id }
async function resolveDepartmentOrgId(medplum) {
  const user = await deriveCurrentUser(medplum);
  const slug = user && user.department;
  if (!slug) return null;
  if (deptOrgIdCache && deptOrgIdCache.slug === slug) return deptOrgIdCache.id;
  const org = await medplum.searchOne(
    'Organization', `identifier=${DEPT_SLUG_SYSTEM}|${encodeURIComponent(slug)}`);
  const id = (org && org.id) || null;
  deptOrgIdCache = { slug, id };
  return id;
}

// Escapes a user search term for a FHIR _filter string literal (double-quote
// delimited) — backslash and double-quote are the only metacharacters.
const filterLiteral = (s) => String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

// Builds the _filter OR expression covering name / phone / MRN. Names and
// phones use "co" (contains). MRN uses "eq": on this Medplum, `identifier co`
// is a DEAD branch — token params don't do substring matching, so `co` returns
// zero for every MRN while `eq` matches the full value (measured live on the
// 265-patient rig 2026-07-11; same root cause as the family-panel search fix).
function searchFilter(term) {
  const lit = filterLiteral(term.trim());
  return `name co "${lit}" or telecom co "${lit}" or identifier eq "${lit}"`;
}

/**
 * Loads ONE page of the patient directory as the logged-in user.
 *
 * @param {object}  opts
 * @param {number}  opts.page      1-based page number (default 1).
 * @param {number}  opts.pageSize  rows per page (default PATIENTS_PAGE_SIZE).
 * @param {string}  opts.search    free-text: name / partial phone / MRN.
 * @param {string}  opts.scope     'all' | 'mine' (see block comment).
 *
 * Returns the same DTO shape the screen already consumes, now carrying paging
 * meta so a cached page-1 render is self-describing:
 *   { source, patients: { rows, counts, page, pageSize, total, paged },
 *     currentUser, hospital }
 * `paged` is ALWAYS true now — both 'all' and 'mine' are server-paged the same
 * way (same _count/_offset/_total/_sort/_revinclude), so the pager renders
 * consistently in every scope (it self-hides when there is only one page).
 *
 * Bridge SWR calls this with no args → page 1, no search, scope defaulting to
 * 'all' (the widest coherent first paint; the screen then re-runs the loader
 * for the user's actual default scope). Page>1 and search are driven by the
 * screen calling this directly — only page 1 rides the SWR cache.
 */
export async function loadPatientsFromMedplum(opts = {}) {
  const { medplum, hospital } = await getMedplum();
  const page = Math.max(1, Number(opts.page) || 1);
  const pageSize = Number(opts.pageSize) || PATIENTS_PAGE_SIZE;
  const search = String(opts.search || '').trim();
  const scope = opts.scope || 'all';
  const deptScope = scope === 'mine';

  // ── Resolve MY department Organization for the 'mine' reverse-chain. ───────
  // Only 'mine' needs it. If the user has no department (owner/admin/
  // front-desk), there is no department set to show → return an honest empty
  // paged view rather than silently widening to everyone.
  let deptOrgId = null;
  if (deptScope) {
    deptOrgId = await resolveDepartmentOrgId(medplum);
    if (!deptOrgId) {
      return {
        source: 'medplum-client-shaping',
        patients: { rows: [], counts: { total: 0, withEncounters: null, withoutEncounters: null }, page: 1, pageSize, total: 0, paged: true, scope },
        currentUser: await deriveCurrentUser(medplum), hospital,
      };
    }
  }

  // ── ONE server-side PAGED Patient query for BOTH scopes ───────────────────
  // One page of patients + only THIS page's encounters (fanned in via
  // _revinclude, which on a paged Patient query pulls just the shown patients'
  // encounters). _total=accurate gives the pager its "of N". 'mine' adds the
  // department reverse-chain so the same pagination, sort, and shaping apply
  // identically to both scopes — the ONLY difference is the extra filter.
  const params = new URLSearchParams();
  params.set('_count', String(pageSize));
  params.set('_offset', String((page - 1) * pageSize));
  params.set('_total', 'accurate');
  params.set('_sort', 'name');
  params.set('_revinclude', 'Encounter:subject');
  // Department filter (functional scoping, not a security re-check): patients
  // who have an Encounter served by my department Organization.
  if (deptScope) params.append('_has:Encounter:subject:service-provider', 'Organization/' + deptOrgId);
  // Search ANDs on top (in 'mine' it stays department-scoped; in 'all' it spans
  // every patient) — name / partial phone / MRN via one contains-OR _filter.
  if (search) params.set('_filter', searchFilter(search));

  const bundle = await medplum.search('Patient', params.toString());
  const total = typeof bundle.total === 'number' ? bundle.total : null;
  const { patients, encountersByPatient } = groupPatientBundle(bundle);
  const rows = patients.map((p) => shapePatientRow(p, encountersByPatient['Patient/' + p.id] || []));

  // `total` is the real (accurate, from _total=accurate) size of the CURRENT
  // scope — every-patient for 'all', my-department for 'mine' — and is what the
  // pager + the "patients on file" KPI show. The with/without-encounter
  // sub-counts USED to be computed over a full ~1000-row payload; now that we
  // deliberately never load a whole scope, they're unknowable cheaply — so we
  // return null for BOTH scopes and the screen hides those two sub-tiles rather
  // than fabricate page-scoped numbers that would read as clinic-wide.
  const counts = {
    total: total != null ? total : rows.length,
    withEncounters: null,
    withoutEncounters: null,
  };

  // No generatedAt: the payload must be deterministic for a given data state —
  // the SWR layer diffs cached vs fresh by JSON equality, and a per-call
  // timestamp would make every revalidate look like a change.
  return {
    source: 'medplum-client-shaping',
    patients: { rows, counts, page, pageSize, total: total != null ? total : rows.length, paged: true, scope },
    currentUser: await deriveCurrentUser(medplum), hospital,
  };
}
